import { Injectable, inject } from '@angular/core';
import { dsApi, smbApi, smbDtos } from '@vectronic/openApi-admin';
import { webEnums } from '@vectronic/openApi-customer';
import { UploadDevice, getCurrentTimespan } from '@vectronic/util';
import JSZip from 'jszip';
import moment from 'moment';
import { Observable, forkJoin, from, map, switchMap } from 'rxjs';
import { create } from 'xmlbuilder2';

@Injectable({ providedIn: 'root' })
export class CreateKeyFileService {
  private pandoraCollarKeyApi = inject(smbApi.PandoraCollarKeys);
  private longRangeDeviceApi = inject(dsApi.LongRangeDevices);

  createKeyFileArchive(
    devices: UploadDevice[],
    fakturaId: string,
    prioNo: number,
    configNo: number,
    deviceType: string,
    createImportFolder: boolean,
    createSalesFolder: boolean,
    warnOnUnregisteredDevices: boolean,
  ): Observable<Blob> {
    const destFolder =
      prioNo != undefined && configNo != undefined
        ? fakturaId + ' ' + prioNo + '-' + configNo
        : fakturaId;
    if (warnOnUnregisteredDevices) {
      if (!devices.every((device) => device.uploadSuccess === true)) {
        alert('Attention: at least one device was not successfully registered in the DSS');
      }
    }
    const zip = new JSZip();
    if (createImportFolder) zip.folder('_import');
    if (createSalesFolder) zip.folder(destFolder);
    return forkJoin(
      ...devices.map((device) =>
        this.longRangeDeviceApi
          .getLongRangeDeviceKey(device.idDevice)
          .pipe(map((key) => ({ key, device }))),
      ),
    ).pipe(
      switchMap((devicesWithKeys) => {
        devicesWithKeys.forEach((deviceWithKey) => {
          if (createSalesFolder) {
            zip.file(
              destFolder + '/Collar' + deviceWithKey.device.idDevice + '_Registration.keyx',
              this.createXmlForSales(deviceWithKey.device, deviceType, deviceWithKey.key),
            );
          }
          if (createImportFolder) {
            zip.file(
              '_import/Collar' + deviceWithKey.device.idDevice + '_Registration.keyx',
              this.createXmlForImport(deviceWithKey.device, deviceType, deviceWithKey.key),
            );
          }
        });
        if (createSalesFolder) {
          zip.file(
            destFolder +
              '/_' +
              fakturaId +
              '_Devicelist-' +
              moment().format('DD-MM-YYYY hh-mm-ss') +
              '.txt',
            devicesWithKeys.map(({ device }) => device.idDevice).join(','),
          );
        }
        return from(zip.generateAsync({ type: 'blob' }));
      }),
    );
  }

  createXmlForGpsXHttpRegistration(
    devices: (UploadDevice & { key: string; deviceType: string })[],
  ): string {
    let root = create().ele('collarList', {
      lastModifiedTimestamp: moment().format('YYYY-MM-DD HH:mm:ss'),
    });
    devices.forEach((device) => {
      root = root.ele('collar', { ID: device.idDevice });
      if (getCurrentTimespan(device.comIdTimeline)?.data && this.translateComType(device.comType)) {
        root = root
          .ele('comIDList')
          .ele('comID', { comType: this.translateComType(device.comType) })
          .txt(getCurrentTimespan(device.comIdTimeline).data)
          .up()
          .up();
      }
      root = root
        .ele('key')
        .txt(device.key)
        .up()
        .ele('storageIDList')
        .ele('sID')
        .txt('4')
        .up()
        .up()
        .ele('collarType')
        .txt(device.deviceType)
        .up()
        .up();
    });
    return root.up().end({ prettyPrint: true }).replace('<?xml version="1.0"?>', '');
  }

  createXmlForSales(device: UploadDevice, deviceType: string, key: string): string {
    let root = create({ version: '1.0', encoding: 'utf-8' })
      .ele('collarKey')
      .ele('collar', { ID: device.idDevice });
    if (getCurrentTimespan(device.comIdTimeline)?.data && this.translateComType(device.comType)) {
      root = root
        .ele('comIDList')
        .ele('comID', { comType: this.translateComType(device.comType) })
        .txt(getCurrentTimespan(device.comIdTimeline).data)
        .up()
        .up();
    }
    root = root.ele('key').txt(key).up().ele('collarType').txt(deviceType).up().up().up();
    return root.end({ prettyPrint: true });
  }

  createXmlForImport(
    { comIdTimeline, comType, idDevice }: UploadDevice,
    deviceType: string,
    key: string,
  ) {
    let root = create({ version: '1.0', encoding: 'utf-8' })
      .ele('collarKey')
      .ele('collar', { ID: idDevice });
    if (getCurrentTimespan(comIdTimeline)?.data && this.translateComType(comType)) {
      root = root
        .ele('comIDList')
        .ele('comID', { comType: this.translateComType(comType) })
        .txt(getCurrentTimespan(comIdTimeline).data)
        .up()
        .up();
    }
    root = root
      .ele('key')
      .txt(key)
      .up()
      .ele('storageIDList')
      .ele('sID')
      .txt('4')
      .up()
      .up()
      .ele('collarType')
      .txt(deviceType)
      .up()
      .up()
      .up();
    return root.end({ prettyPrint: true });
  }

  translateComType(comType: webEnums.ComType) {
    if (comType === 'IRIDIUM') return 'Iridium';
    else if (comType === 'GLOBALSTAR') return 'Globalstar';
    else if (comType === 'GSM') return 'GSM-6bit';
    return '';
  }

  storeKeyFilesAndDeviceIdList(
    devices: UploadDevice[],
    fakturaId: string,
    prioNo: number,
    configNo: number,
    deviceType: string,
    successCallback: (result: smbDtos.StoreDirectoryResult) => void,
    failureCallback: (error: any) => void,
  ) {
    this.createKeyFileArchive(
      devices,
      fakturaId,
      prioNo,
      configNo,
      deviceType,
      false,
      true,
      false,
    ).subscribe((zip) => {
      this.pandoraCollarKeyApi
        .storeDirectory(fakturaId, prioNo, configNo, zip)
        .subscribe(successCallback, failureCallback);
    });
  }

  storeDropOffIdList(
    dropOffs: UploadDevice[],
    fakturaId: string,
    prioNo: number,
    configNo: number,
  ): Observable<smbDtos.StoreDirectoryResult> {
    const destFolder =
      prioNo != undefined && configNo != undefined
        ? fakturaId + ' ' + prioNo + '-' + configNo
        : fakturaId;
    const zip = new JSZip();
    zip.file(
      destFolder +
        '/_' +
        fakturaId +
        '_DropOffIdList-' +
        moment().format('DD-MM-YYYY hh-mm-ss') +
        '.txt',
      dropOffs.map((d) => d.idDevice).join(','),
    );

    return from(zip.generateAsync({ type: 'blob' })).pipe(
      switchMap((zip) => this.pandoraCollarKeyApi.storeDirectory(fakturaId, prioNo, configNo, zip)),
    );
  }
}
